/**
 * Live preview of an itemised AR/AP invoice — mirrors
 * packages/modules/accounting/src/subledger/open-item-lines.ts (quantity ×
 * unit price, line discount, document discount spread by largest remainder,
 * per-line VAT, ROUND_HALF_UP). Preview only: the server recomputes and is
 * authoritative. All amounts are integer Rial `bigint`, never `Number`.
 */

export interface InvoiceDraftLine {
  readonly lineKind: 'item' | 'charge';
  readonly quantity: string;
  readonly unitPriceRial: string;
  readonly discountRial: string;
}

export interface InvoicePreviewLine {
  readonly grossRial: bigint;
  readonly lineDiscountRial: bigint;
  readonly documentDiscountRial: bigint;
  readonly taxableRial: bigint;
  readonly vatRial: bigint;
  readonly totalRial: bigint;
  readonly error: string | null;
}

export interface InvoicePreview {
  readonly lines: readonly InvoicePreviewLine[];
  readonly grossRial: bigint;
  readonly discountRial: bigint;
  readonly chargesRial: bigint;
  readonly taxableRial: bigint;
  readonly vatRial: bigint;
  readonly totalRial: bigint;
  readonly error: string | null;
}

const QUANTITY = /^(\d{1,12})(?:\.(\d{1,4}))?$/;

/** Round a non-negative exact fraction half-up to whole Rial. */
export function roundHalfUp(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

export function parseQuantity(text: string): { num: bigint; den: bigint } | null {
  const match = QUANTITY.exec(text.trim());
  if (!match) return null;
  const fraction = match[2] ?? '';
  const num = BigInt(match[1]! + fraction);
  if (num <= 0n) return null;
  return { num, den: 10n ** BigInt(fraction.length) };
}

function digits(value: string): bigint {
  return /^\d+$/.test(value) ? BigInt(value) : 0n;
}

export function allocateProportionally(total: bigint, weights: readonly bigint[]): bigint[] {
  const weightSum = weights.reduce((s, w) => s + w, 0n);
  if (total === 0n || weights.length === 0 || weightSum === 0n) return weights.map(() => 0n);
  const shares = weights.map((w) => (total * w) / weightSum);
  let remainder = total - shares.reduce((s, v) => s + v, 0n);
  const order = weights
    .map((w, i) => ({ i, frac: (total * w) % weightSum }))
    .sort((a, b) => (a.frac === b.frac ? a.i - b.i : a.frac > b.frac ? -1 : 1));
  for (const { i } of order) {
    if (remainder === 0n) break;
    shares[i] = shares[i]! + 1n;
    remainder -= 1n;
  }
  return shares;
}

export function previewInvoice(input: {
  readonly lines: readonly InvoiceDraftLine[];
  readonly documentDiscountRial: string;
  readonly taxRate: { readonly numerator: bigint; readonly denominator: bigint } | null;
}): InvoicePreview {
  const drafts = input.lines.map((line) => {
    const qty = parseQuantity(line.quantity);
    const price = digits(line.unitPriceRial);
    const discount = line.lineKind === 'item' ? digits(line.discountRial) : 0n;
    // A line the user has not priced yet is blank, not an error; it is not submitted.
    if (line.unitPriceRial.trim() === '') {
      return { line, gross: 0n, discount: 0n, error: null as string | null };
    }
    if (!qty) return { line, gross: 0n, discount, error: 'مقدار نامعتبر است' };
    const gross = roundHalfUp(price * qty.num, qty.den);
    if (gross <= 0n) return { line, gross, discount, error: 'مبلغ ردیف باید مثبت باشد' };
    if (discount >= gross) {
      return { line, gross, discount, error: 'تخفیف باید کمتر از مبلغ ردیف باشد' };
    }
    return { line, gross, discount, error: null as string | null };
  });

  const documentDiscount = digits(input.documentDiscountRial);
  const itemIndexes = drafts.flatMap((d, i) => (d.line.lineKind === 'item' ? [i] : []));
  const nets = itemIndexes.map((i) => drafts[i]!.gross - drafts[i]!.discount);
  const netTotal = nets.reduce((s, v) => s + v, 0n);
  let error: string | null = null;
  if (documentDiscount > 0n && documentDiscount >= netTotal) {
    error = 'تخفیف کل فاکتور باید کمتر از جمع اقلام باشد';
  }
  const shares = allocateProportionally(error ? 0n : documentDiscount, nets);
  const shareByIndex = new Map(itemIndexes.map((li, k) => [li, shares[k]!]));

  const lines: InvoicePreviewLine[] = drafts.map((d, i) => {
    const documentDiscountRial = shareByIndex.get(i) ?? 0n;
    const taxable = d.error ? 0n : d.gross - d.discount - documentDiscountRial;
    const vat =
      input.taxRate && taxable > 0n
        ? roundHalfUp(taxable * input.taxRate.numerator, input.taxRate.denominator)
        : 0n;
    return {
      grossRial: d.gross,
      lineDiscountRial: d.discount,
      documentDiscountRial,
      taxableRial: taxable,
      vatRial: vat,
      totalRial: taxable + vat,
      error: d.error ?? (taxable <= 0n && d.gross > 0n ? 'ردیف کاملاً تخفیف خورده است' : null),
    };
  });

  const sum = (f: (l: InvoicePreviewLine) => bigint, filter?: (i: number) => boolean) =>
    lines.reduce((s, l, i) => (filter && !filter(i) ? s : s + f(l)), 0n);
  const isItem = (i: number) => drafts[i]!.line.lineKind === 'item';
  return {
    lines,
    grossRial: sum((l) => l.grossRial, isItem),
    discountRial: sum((l) => l.lineDiscountRial + l.documentDiscountRial),
    chargesRial: sum(
      (l) => l.grossRial,
      (i) => !isItem(i),
    ),
    taxableRial: sum((l) => l.taxableRial),
    vatRial: sum((l) => l.vatRial),
    totalRial: sum((l) => l.totalRial),
    error: error ?? lines.find((l) => l.error)?.error ?? null,
  };
}
